import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { STRINGS } from '../locales/shopStrings';

const ShopLanguageContext = createContext(null);

const STORAGE_KEY = 'shopLocale';

function getNested(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((o, k) => (o != null && o[k] !== undefined ? o[k] : undefined), obj);
}

export function ShopLanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      if (s === 'en' || s === 'mr' || s === 'kn') return s;
    } catch {
      /* ignore */
    }
    return 'en';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale === 'mr' ? 'mr' : locale === 'kn' ? 'kn' : 'en';
    root.setAttribute('data-locale', locale);
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (next !== 'en' && next !== 'mr' && next !== 'kn') return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const enMap = STRINGS.en;
      const locMap = STRINGS[locale] || enMap;
      let str = getNested(locMap, key) ?? getNested(enMap, key) ?? key;
      if (typeof str !== 'string') return key;
      if (vars && typeof vars === 'object') {
        str = str.replace(/\{(\w+)\}/g, (_, k) =>
          vars[k] != null ? String(vars[k]) : `{${k}}`
        );
      }
      return str;
    },
    [locale]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
    }),
    [locale, setLocale, t]
  );

  return (
    <ShopLanguageContext.Provider value={value}>{children}</ShopLanguageContext.Provider>
  );
}

export function useShopLanguage() {
  const ctx = useContext(ShopLanguageContext);
  if (!ctx) {
    throw new Error('useShopLanguage must be used within ShopLanguageProvider');
  }
  return ctx;
}
